import React, { useRef, useState, useEffect, useCallback, useLayoutEffect, useMemo, } from 'react';
import { View, Platform, Keyboard } from 'react-native';
import Animated, { useSharedValue, useAnimatedScrollHandler, useAnimatedStyle, interpolateColor, } from 'react-native-reanimated';
import { debounce } from 'lodash';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FlashList } from '@shopify/flash-list';
import { KeyboardAwareScrollView, } from 'react-native-keyboard-controller';
import SearchBar from 'design-system/SearchBar';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system/values';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { AppView } from 'design-system/AppView';
import Indicator from 'design-system/Indicator';
import { CardView } from 'design-system/CardView';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import ListEmptyView from 'design-system/ListEmptyView';
import EmptyComponent from 'design-system/EmptyComponent';
import { TextButton } from 'design-system/TextButton';
import { NavigationHeader } from 'design-system/NavigationHeader';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import { getSearchTransactions } from 'actions';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Modal from 'utils/packages/Modal';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { formatAmount, getOrdinalSuffix } from 'utils/formatting';
import { searchSubscriptions, addTxnsToSubscription, cleanSubscriptionSearch, getSubscriptionsById, editSubscription, } from '../actions';
import { makeSelectSubscriptionById } from '../selectors';
import { getMatchingRuleSuggestions, } from '../utils/matchingRules';
import MatchingRuleSuggestionContent from '../components/modal/MatchingRuleSuggestionContent';
import { isWeb } from '../../../constants';
import RowSubscriptionTransaction from '../components/transactions/RowSubscriptionTransaction';
const keyExtractor = (item) => item.id.toString();
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const SubtitleAmount = ({ children }) => (<Text NumbersThin-14 Gray>
    {children}
  </Text>);
const strings = {
    trackingPayment: 'Tracking payment',
    trackingPaymentMessage: 'You are already tracking this recurring payment',
    editPayment: 'Edit this recurring payment',
    cantFindPayment: "Can't find the payment you made?",
    updateRules: {
        amountTitle: 'Update your amount range?',
        maxLimitTitle: 'Update your max limit?',
        minLimitTitle: 'Update your min limit?',
        dateTitle: 'Update your payment date?',
        bothTitle: 'Update your matching rules?',
        amountBody: (amount, direction, min, max) => (<>
        This <SubtitleAmount>{amount}</SubtitleAmount> payment was {direction}{' '}
        your amount range of <SubtitleAmount>{min}</SubtitleAmount> to{' '}
        <SubtitleAmount>{max}</SubtitleAmount>, so we missed it. Update the
        range and we&apos;ll catch it automatically next time.
      </>),
        maxLimitBody: (amount, max) => (<>
        This <SubtitleAmount>{amount}</SubtitleAmount> payment was over your max
        limit of <SubtitleAmount>{max}</SubtitleAmount>, so we missed it. Update
        the limit and we&apos;ll catch it automatically next time.
      </>),
        minLimitBody: (amount, min) => (<>
        This <SubtitleAmount>{amount}</SubtitleAmount> payment was under your
        minimum limit of <SubtitleAmount>{min}</SubtitleAmount>, so we missed
        it. Update the limit and we&apos;ll catch it automatically next time.
      </>),
        dateBody: (day) => `This payment arrived on the ${day}, which is outside your payment date, so we missed it. Update it and we'll catch it automatically next time.`,
        bothBody: "This payment didn't match your current settings, so we missed it. Update your rule and we'll catch it automatically next time.",
        keepRules: 'Only track this payment',
        update: 'Yes, update',
    },
};
const describeAmountSuggestion = (amount, currency) => {
    const actualAmount = formatAmount(amount.actualAmount, currency, 2);
    const { minAmount, maxAmount } = amount.current;
    if (minAmount != null && maxAmount != null) {
        return {
            title: strings.updateRules.amountTitle,
            subtitle: strings.updateRules.amountBody(actualAmount, amount.direction, formatAmount(minAmount, currency, null), formatAmount(maxAmount, currency, null)),
        };
    }
    if (maxAmount != null) {
        return {
            title: strings.updateRules.maxLimitTitle,
            subtitle: strings.updateRules.maxLimitBody(actualAmount, formatAmount(maxAmount, currency, null)),
        };
    }
    return {
        title: strings.updateRules.minLimitTitle,
        subtitle: strings.updateRules.minLimitBody(actualAmount, formatAmount(minAmount, currency, null)),
    };
};
const describeRuleSuggestions = (suggestions, currency) => {
    const { amount, monthDay } = suggestions;
    if (amount && monthDay) {
        return {
            title: strings.updateRules.bothTitle,
            subtitle: strings.updateRules.bothBody,
        };
    }
    if (amount) {
        return describeAmountSuggestion(amount, currency);
    }
    const day = monthDay?.actualDay;
    return {
        title: strings.updateRules.dateTitle,
        subtitle: strings.updateRules.dateBody(`${day}${getOrdinalSuffix(day)}`),
    };
};
const SubscriptionSearchModalScreen = ({ navigation, route }) => {
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Search',
            headerShown: false,
            gestureEnabled: false,
            headerLeft: EmptyComponent,
        });
    }, []);
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { subscriptionId, empty, resolveMissedPayment, income = false, } = route.params;
    const subscriptionsSearch = useAppSelector((store) => store.subscriptions.subscriptionsSearch);
    const memoSelectSubscriptionById = useMemo(makeSelectSubscriptionById, []);
    const subscription = useAppSelector((store) => memoSelectSubscriptionById(store, subscriptionId));
    const isSearchingSubscription = useAppSelector((store) => store.subscriptions.isSearchingSubscription);
    const refSearchBar = useRef(null);
    const [text, setText] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [isAddingTransactions, setIsAddingTransactions] = useState(false);
    const showButton = !!(subscriptionId && selectedIds.length);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', showButton ? HEIGHT_PLUS_PADDING : SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(cleanSubscriptionSearch());
    }, []);
    const filterResponse = useCallback((text) => {
        setText(text);
        if (text.length <= 1) {
            dispatch(cleanSubscriptionSearch());
            return;
        }
        dispatch(searchSubscriptions(text, income));
    }, [income]);
    const onChangeText = debounce(filterResponse, 150);
    const openSubscription = useRequiredBenefitCallbackWithSpaces(useCallback((item) => {
        if (item.subscriptionId === null) {
            return;
        }
        navigation.navigateDeprecated('Subscription', {
            id: item.subscriptionId,
        });
    }, []), 'recurringPayments');
    const onSelected = useCallback((data) => {
        if (data.subscriptionId && 'isRecurring' in data && data.isRecurring) {
            Keyboard.dismiss();
            Modal.showConfirmation({
                title: strings.trackingPayment,
                subtitle: strings.trackingPaymentMessage,
                confirmButtonTitle: strings.editPayment,
                onConfirm: () => openSubscription(data),
                brandReversedConfirm: true,
            });
            return;
        }
        if (subscriptionId) {
            if (selectedIds.includes(data?.id)) {
                setSelectedIds((oldState) => oldState.filter((id) => id !== data?.id));
                setSelectedTransaction(null);
            }
            else if (resolveMissedPayment) {
                // Resolving a missed payment means identifying the one
                // transaction that paid it — replace any previous selection.
                setSelectedIds([data?.id]);
                setSelectedTransaction(data);
            }
            else {
                setSelectedIds((oldState) => [...oldState, data?.id]);
            }
        }
        else {
            navigation.navigateDeprecated('RenameSubscription', {
                createTransaction: data,
            });
        }
    }, [openSubscription, selectedIds, subscriptionId, resolveMissedPayment]);
    const onCancelPressed = useCallback(() => {
        navigation.goBack();
    }, []);
    const addSelectedTransactions = useCallback(async () => {
        if (subscriptionId && selectedIds.length) {
            navigation.goBack();
            await dispatch(addTxnsToSubscription(subscriptionId, selectedIds));
            dispatch(getSearchTransactions());
            if (resolveMissedPayment) {
                await dispatch(getSubscriptionsById(subscriptionId));
            }
        }
    }, [selectedIds, subscriptionId, resolveMissedPayment]);
    const applySuggestedRules = useCallback(async (suggestions) => {
        if (!subscriptionId)
            return;
        const body = {};
        if (suggestions.amount) {
            const { minAmount, maxAmount } = suggestions.amount.suggested;
            if (minAmount != null) {
                body.minAmount = String(minAmount);
            }
            else if (suggestions.amount.direction === 'below') {
                body.minAmount = null;
            }
            if (maxAmount != null)
                body.maxAmount = String(maxAmount);
        }
        if (suggestions.monthDay) {
            body.minMonthDay = suggestions.monthDay.suggested.minMonthDay;
            body.maxMonthDay = suggestions.monthDay.suggested.maxMonthDay;
        }
        await dispatch(editSubscription(subscriptionId, body));
    }, [subscriptionId]);
    const onPressSetRecurring = useCallback(() => {
        if (!resolveMissedPayment) {
            addSelectedTransactions();
            return;
        }
        const suggestions = subscription && selectedTransaction
            ? getMatchingRuleSuggestions(subscription, selectedTransaction)
            : null;
        // The transaction fits the existing rules — nothing to update.
        if (!suggestions) {
            addSelectedTransactions();
            return;
        }
        Keyboard.dismiss();
        const currency = selectedTransaction?.nativeAmount?.currency ??
            selectedTransaction?.currency;
        const { title, subtitle } = describeRuleSuggestions(suggestions, currency);
        Modal.showConfirmation({
            title,
            subtitle: (<MatchingRuleSuggestionContent subtitle={subtitle} suggestions={suggestions} currency={currency}/>),
            confirmButtonTitle: strings.updateRules.keepRules,
            cancelButtonTitle: strings.updateRules.update,
            onConfirm: () => {
                Modal.hide();
                addSelectedTransactions();
            },
            onPressCancel: async () => {
                Modal.hide();
                setIsAddingTransactions(true);
                try {
                    await applySuggestedRules(suggestions);
                    await addSelectedTransactions();
                }
                finally {
                    setIsAddingTransactions(false);
                }
            },
        });
    }, [
        addSelectedTransactions,
        applySuggestedRules,
        resolveMissedPayment,
        selectedTransaction,
        subscription,
    ]);
    const renderRow = useCallback(({ item, index, }) => (<View style={[
            styles.rowTxnContainer,
            index === 0 && styles.itemContainerFirst,
            index === subscriptionsSearch.length - 1 &&
                styles.itemContainerLast,
        ]}>
          <RowSubscriptionTransaction data={item} onPress={onSelected} canSelect isMultipleSelect={!resolveMissedPayment} selected={selectedIds.includes(item.id)} isTracking={item.isRecurring} isIncome={subscription?.isIncome}/>
        </View>), [
        onSelected,
        resolveMissedPayment,
        selectedIds,
        styles.itemContainerFirst,
        styles.itemContainerLast,
        styles.rowTxnContainer,
        subscriptionsSearch.length,
        subscription?.isIncome,
    ]);
    const onPressCantFindPayment = useCallback(() => {
        navigation.navigateDeprecated('MissingPaymentReasons');
    }, []);
    const renderCantFindPayment = useCallback((extraPadding) => (<View style={[
            styles.cantFindContainer,
            extraPadding ? styles.cantFindContainerExtraPadding : null,
        ]}>
          <TextButton text={strings.cantFindPayment} onPress={onPressCantFindPayment}/>
        </View>), [
        onPressCantFindPayment,
        styles.cantFindContainer,
        styles.cantFindContainerExtraPadding,
    ]);
    const allResultsTracking = subscriptionsSearch.length > 0 &&
        subscriptionsSearch.every((trx) => trx.isRecurring);
    const renderEmpty = useCallback(() => {
        if (!text.length) {
            return (<CardView>
            <ListEmptyView alt onCard happy title={empty.title} subTitle={empty.subtitle}/>
          </CardView>);
        }
        if (!isSearchingSubscription) {
            return (<>
            <CardView>
              <ListEmptyView alt onCard title="No results found"/>
            </CardView>
            {resolveMissedPayment ? renderCantFindPayment(false) : null}
          </>);
        }
        return (<View style={styles.indicatorContainer}>
          <Indicator dark/>
        </View>);
    }, [
        isSearchingSubscription,
        styles.indicatorContainer,
        text.length,
        empty,
        renderCantFindPayment,
        resolveMissedPayment,
    ]);
    const scrollY = useSharedValue(0);
    const onScroll = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    useAutoFocusInput(refSearchBar);
    const { top } = useSafeAreaInsets();
    const textInputStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.cards.onDark,
            colors.background.dark,
        ]),
    }), [colors.background.dark, colors.cards.onDark, scrollY]);
    const baseProps = {
        onScroll,
        keyExtractor,
        renderItem: renderRow,
        scrollEventThrottle: 16,
        data: subscriptionsSearch,
        ListEmptyComponent: renderEmpty,
        ListHeaderComponent: resolveMissedPayment &&
            allResultsTracking &&
            text.length &&
            subscriptionsSearch.length > 0
            ? renderCantFindPayment(true)
            : null,
        contentContainerStyle: paddingBottom,
        keyboardDismissMode: 'on-drag',
        keyboardShouldPersistTaps: 'always',
        scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
        showsVerticalScrollIndicator: true,
    };
    const renderScrollComponent = useCallback((props) => (<KeyboardAwareScrollView {...props} bottomOffset={rem(80)} mode="layout"/>), []);
    return (<AppView withFrame={false}>
        <NavigationHeader noPaddingTop skipSafeArea scrollY={scrollY} withFrame isInTab={isWeb} regularTitleHeight>
          <Spacer h={isWeb ? rem(16) : Math.max(36, top)}/>
          <SearchBar ref={refSearchBar} forSearchHeader alwaysShowCancel onCancel={onCancelPressed} onChangeSearchTerm={onChangeText} textInputBgStyles={textInputStyle}/>
        </NavigationHeader>
        {isWeb ? (<Animated.FlatList {...baseProps}/>) : (<AnimatedFlashList {...baseProps} extraData={selectedIds} renderScrollComponent={renderScrollComponent}/>)}
        {showButton ? (<KeyboardAvoidingButton brand title="Continue" isFetching={isAddingTransactions} onPress={onPressSetRecurring}/>) : null}
      </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
    },
    rowTxnContainer: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
    itemContainerFirst: {
        marginTop: rem(16),
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(Platform.OS !== 'android' && {
            overflow: 'hidden',
        }),
        ...(isWeb && {
            paddingTop: rem(8),
        }),
    },
    itemContainerLast: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(Platform.OS !== 'android' && {
            overflow: 'hidden',
        }),
        ...(isWeb && {
            paddingBottom: rem(8),
        }),
    },
    indicatorContainer: {
        marginTop: rem(48),
    },
    cantFindContainer: {
        alignItems: 'center',
        paddingVertical: rem(8),
    },
    cantFindContainerExtraPadding: {
        paddingTop: rem(16),
    },
}));
export default SubscriptionSearchModalScreen;
